import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Col,
    Row,
    Form,
    Grid,
    Space,
    Input,
    Button,
    DatePicker,
    Select,
    Divider,
    Table,
    FormProps,
    message,
} from 'antd';
import { ExportOutlined, SearchOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import isEmail from 'validator/lib/isEmail';
import { useGetInvoicesV2 } from '@/store/invoice/useGetInvoicesV2';
import { useFlags } from '@/contexts/Flags';
import { topTitleContext } from '../../contexts/TopTitle';
import { authContext } from '../../contexts/Auth';
import { CheckBox } from '../../components/DataInput';
import * as S from './style';
import { columnsTicket } from './columnsInvoice';
import { columnsCharge } from './columnsCharge';
import { FiltersInvoice } from '../../store/invoice/useGetInvoice';
import { ExportFile } from './schemaFile';
import { FiltersCharge, useGetCharge } from '../../store/invoice/useGetCharge';
import { useGetAllStore } from '../../store/Store';

const { Option } = Select;

type FieldType = {
    shopManagerId?: string;
    email?: string;
    month?: Moment;
    statusTicket?: string;
    doc: string;
    stores: Array<number> | undefined;
};

const optionsStatus = [
    { label: 'Pago', value: 'PAGO' },
    { label: 'Cancelado', value: 'CANCELADO' },
    { label: 'Vence Hoje', value: 'VENCEHOJE' },
    { label: 'À Vencer', value: 'AVENCER' },
    { label: 'Atrasado', value: 'ATRASADO' },
    { label: 'Parcialmente Pago', value: 'PAGTO_PARCIAL' },
];

const Ticket = () => {
    const { titleValue } = useContext(topTitleContext);
    const breakpoints = Grid.useBreakpoint();

    const [form] = Form.useForm();

    const { isAdmin } = useContext(authContext);

    const [, setStoresSelected] = useState();

    const flags = useFlags();

    const [filters, setFilters] = useState<FiltersInvoice>({
        'filters[shopmanagerId]': '',
        'filters[shopmanagerEmail]': '',
        'filters[month]': '',
        'filters[status]': '',
        page: 1,
        perPage: 100,
    });

    const [filtersCharge, setFiltersCharge] = useState<FiltersCharge>({
        date: undefined,
        'stores[]': undefined,
        shopmanagerEmail: undefined,
        email: undefined,
        page: 1,
        perPage: 10,
    });

    const [selectOption, setSelectionOption] = useState<'invoice' | 'charges'>(
        'invoice',
    );

    /*
     * case @Admin
     * should only call the api if the user enters the merchant's ID or email
     */
    const emailOrIdFilled =
        filters['filters[shopmanagerEmail]'] !== '' ||
        filters['filters[shopmanagerId]'] !== '';

    const enableRequest = !isAdmin ? true : !!(isAdmin && emailOrIdFilled);

    const { data: invoice, isLoading } = useGetInvoicesV2(
        filters,
        enableRequest,
    );

    const invoiceWithBarCode = useMemo(
        () => invoice?.data.filter((item) => !!item.barcode),
        [invoice],
    );

    const {
        data: charges,
        isLoading: isLoadingCharges,
        error: errorFetchCharge,
    } = useGetCharge(filtersCharge, selectOption === 'charges');

    const { data: stores, isLoading: isLoadingStores } = useGetAllStore();

    const storesOptions = stores?.map((store) => ({
        label: store.name,
        value: `${store.id}`,
    }));

    const filterOption = (
        input: string,
        option?: { label: string; value: string },
    ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

    const setCurrentPage = (value: number, pzSize: number) => {
        if (selectOption === 'invoice') {
            setFilters({
                ...filters,
                page: value,
                perPage: pzSize,
            });
        }
        if (selectOption === 'charges') {
            setFiltersCharge({
                ...filtersCharge,
                page: value,
                perPage: pzSize,
            });
        }
    };

    const generateFile = () => {
        if (!invoice?.data?.length && !charges?.data.length) {
            message.error('Nenhum dado disponível para ser exportado!');
            return;
        }
        ExportFile(
            selectOption === 'invoice' ? invoice?.data : charges?.data,
            selectOption,
        );
    };

    const handleSearch: FormProps<FieldType>['onFinish'] = (data) => {
        const dateReference = data.month
            ? moment(data.month).format('YYYY-MM-DD')
            : undefined;

        if (selectOption === 'invoice') {
            setFilters({
                ...filters,
                'filters[month]': dateReference,
                'filters[shopmanagerEmail]': data.email,
                'filters[shopmanagerId]': data.shopManagerId,
                'filters[status]': data.statusTicket,
            });
        }

        setFiltersCharge({
            ...filtersCharge,
            'stores[]': data.stores,
            shopmanagerEmail: data.shopManagerId,
            email: data.email,
            date: dateReference,
        });
    };

    useEffect(() => {
        titleValue('Boletos e cobranças');
    }, []);

    useEffect(() => {
        if (errorFetchCharge) {
            message.error('Falha ao buscar cobranças!');
        }
    }, [errorFetchCharge]);

    return (
        <Row style={{ padding: '1rem' }}>
            <Col xs={24} md={23}>
                <Form form={form} layout="vertical" onFinish={handleSearch}>
                    <Row gutter={[0, 0]}>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6 }}
                            style={{
                                paddingLeft: 0,
                                paddingRight: breakpoints.md ? 12 : 0,
                            }}
                        >
                            <Form.Item<FieldType>
                                label="ID lojista"
                                name="shopManagerId"
                                rules={[
                                    {
                                        type: 'string',
                                        validator: (_, value) => {
                                            if (
                                                isAdmin &&
                                                !Number(value) &&
                                                !form.getFieldValue('email') &&
                                                selectOption === 'invoice'
                                            ) {
                                                // eslint-disable-next-line prefer-promise-reject-errors
                                                return Promise.reject(
                                                    'Por favor, informe o id do lojista',
                                                );
                                            }
                                            form.isFieldValidating(
                                                'shopManagerId',
                                            );
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Space
                                    orientation="vertical"
                                    style={{ width: '100%' }}
                                >
                                    <Input disabled={!isAdmin} />
                                </Space>
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6 }}
                            style={{
                                paddingLeft: 0,
                                paddingRight: breakpoints.md ? 12 : 0,
                            }}
                        >
                            <Form.Item<FieldType>
                                label="E-mail lojista"
                                name="email"
                                rules={[
                                    {
                                        type: 'email',
                                        validator: (_, value) => {
                                            if (
                                                isAdmin &&
                                                !form.getFieldValue(
                                                    'shopManagerId',
                                                ) &&
                                                !value &&
                                                selectOption === 'invoice'
                                            ) {
                                                // eslint-disable-next-line prefer-promise-reject-errors
                                                return Promise.reject(
                                                    'Por favor, informe o email do lojista',
                                                );
                                            }
                                            if (
                                                isAdmin &&
                                                !form.getFieldValue(
                                                    'shopManagerId',
                                                ) &&
                                                value &&
                                                !isEmail(value)
                                            ) {
                                                // eslint-disable-next-line prefer-promise-reject-errors
                                                return Promise.reject(
                                                    'Por favor, informe um email válido',
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input disabled={!isAdmin} />
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6 }}
                            style={{
                                paddingLeft: 0,
                                paddingRight: breakpoints.md ? 12 : 0,
                            }}
                        >
                            <Form.Item<FieldType>
                                label={
                                    selectOption === 'invoice'
                                        ? 'Mês de vencimento'
                                        : 'Mês'
                                }
                                name="month"
                            >
                                <DatePicker
                                    picker="month"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6 }}
                            style={{
                                paddingLeft: 0,
                                paddingRight: breakpoints.md ? 12 : 0,
                            }}
                        >
                            {selectOption === 'invoice' && (
                                <Form.Item
                                    label="Status do boleto"
                                    name="statusTicket"
                                >
                                    <Select>
                                        {optionsStatus.map((item) => (
                                            <Option
                                                key={item.value}
                                                value={item.value}
                                            >
                                                {item.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}
                            {selectOption === 'charges' && (
                                <Form.Item label="Loja" name="stores">
                                    <Select
                                        options={storesOptions}
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        style={{ width: '100%' }}
                                        placeholder="Selecionar Loja..."
                                        showSearch
                                        onChange={(store) =>
                                            setStoresSelected(store)
                                        }
                                        disabled={isLoadingStores}
                                        filterOption={filterOption}
                                    />
                                </Form.Item>
                            )}
                        </Col>
                    </Row>
                    <div style={{ marginTop: 18 }}>
                        <Row gutter={[0, 10]}>
                            <Col xs={24} sm={24} md={8} lg={6} xl={6} xxl={4}>
                                <Button
                                    htmlType="submit"
                                    className="default-btn"
                                    icon={<SearchOutlined />}
                                    style={{ width: '100%' }}
                                    type="primary"
                                >
                                    Pesquisar
                                </Button>
                            </Col>
                            <Col
                                xs={24}
                                sm={24}
                                md={{ span: 8, offset: 1 }}
                                lg={{ span: 6, offset: 1 }}
                                xl={{ span: 6, offset: 1 }}
                                xxl={{ span: 4, offset: 1 }}
                            >
                                <Button
                                    htmlType="reset"
                                    danger
                                    style={{ width: '100%' }}
                                    onClick={() => {
                                        setFilters({
                                            ...filters,
                                            'filters[shopmanagerId]': '',
                                            'filters[shopmanagerEmail]': '',
                                            'filters[month]': '',
                                            'filters[status]': '',
                                        });
                                    }}
                                >
                                    Limpar
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </Form>
            </Col>
            <Divider />
            <Col xs={24} md={23}>
                <Row justify="center">
                    {flags && flags?.CHARGE && (
                        <CheckBox
                            title="Boletos"
                            checked={selectOption === 'invoice'}
                            onChange={() => {
                                setSelectionOption('invoice');
                            }}
                        />
                    )}
                    {flags && flags?.CHARGE && (
                        <CheckBox
                            title="Cobranças"
                            checked={selectOption === 'charges'}
                            onChange={() => {
                                setSelectionOption('charges');
                            }}
                        />
                    )}
                </Row>
            </Col>

            {selectOption === 'invoice' && (
                <>
                    <Col span={12} md={12} xl={12} sm={12} xs={24}>
                        <Col xxl={14} xl={14} md={14} sm={24} xs={24}>
                            <S.TitleResult>
                                Atenção: podem haver boletos em aberto não
                                listados na tabela abaixo (na dúvida, consultar
                                seu gerente de contas)
                            </S.TitleResult>
                        </Col>
                    </Col>
                    <Col span={12} md={12} xl={12} sm={12} xs={24}>
                        <Row
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                icon={<ExportOutlined />}
                                className="green-btn"
                                style={{
                                    width: breakpoints.md ? undefined : '100%',
                                    maxWidth: breakpoints.md ? 227 : '100%',
                                }}
                                onClick={generateFile}
                                disabled={
                                    !invoice?.data.length &&
                                    !charges?.data.length
                                }
                            >
                                Exportar em Excel
                            </Button>
                        </Row>
                    </Col>
                </>
            )}
            {selectOption === 'charges' && (
                <Col span={24}>
                    <Row
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                        <Button
                            icon={<ExportOutlined />}
                            className="green-btn"
                            style={{
                                width: breakpoints.md ? undefined : '100%',
                                maxWidth: breakpoints.md ? 227 : '100%',
                            }}
                            onClick={generateFile}
                            disabled={
                                !invoice?.data.length && !charges?.data.length
                            }
                        >
                            Exportar em Excel
                        </Button>
                    </Row>
                </Col>
            )}

            <Col xs={24} style={{ marginTop: 50 }}>
                <S.TitleResult>
                    A pesquisa retornou{' '}
                    {selectOption === 'invoice' && (invoice?.data?.length || 0)}
                    {selectOption === 'charges' && (charges?.data.length || 0)}
                    {} resultado(s)
                </S.TitleResult>
            </Col>
            <div style={{ width: '100%' }}>
                {selectOption === 'invoice' && (
                    <Table
                        columns={columnsTicket}
                        dataSource={invoiceWithBarCode}
                        bordered
                        loading={isLoading && enableRequest}
                        scroll={{ x: true }}
                        pagination={{ pageSize: 10 }}
                    />
                )}
                {selectOption === 'charges' && (
                    <Table
                        columns={columnsCharge}
                        dataSource={charges?.data}
                        bordered
                        loading={isLoadingCharges}
                        tableLayout="fixed"
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            total: charges?.meta?.total,
                            current: charges?.meta?.current_page,
                            onChange: (page, pgSize) => {
                                setCurrentPage(page, pgSize);
                            },
                        }}
                    />
                )}
            </div>
            {/* <Alert message="Seu usuário está bloqueado e por este motivo seu acesso é limitado" /> */}
        </Row>
    );
};

export default Ticket;
